// @ts-nocheck
import {
  getCurrentUser,
  login,
  onUserChange,
  requestPasswordReset,
  resendRegisterOtp,
  resendVerificationEmail,
  resetPassword,
  startRegister,
  type SessionUser,
  verifyRegisterOtp,
} from "../auth/session";
import { mapState } from "../map/state";
import { openPurchaseModal, openCreateMemorial } from "./purchase";
import { clearResetTokenFromUrl } from "./password-reset-deep-link";
import { t } from "../i18n";
import { translateApiError } from "../i18n/api-errors";
import { showToast } from "../components/toast/toast";

declare const lucide: any;

type AuthMode = "login" | "register" | "otp" | "forgot" | "reset";
export type AuthIntent = "purchase" | "general";

let authMode: AuthMode = "login";
let authIntent: AuthIntent = "general";
let pendingResetToken: string | null = null;
let pendingRegistrationEmail: string | null = null;
let verificationBanner: HTMLDivElement | null = null;

function resetAuthResumeState() {
  mapState.pendingPurchaseHole = null;
  authIntent = "general";
}

function show(el: HTMLElement | null, visible: boolean) {
  if (el) el.style.display = visible ? "" : "none";
}

function setAuthMode(mode: AuthMode) {
  authMode = mode;
  const title = document.getElementById("auth-title");
  const submit = document.getElementById("auth-submit");
  const loginTab = document.getElementById("auth-tab-login");
  const registerTab = document.getElementById("auth-tab-register");
  const tabs = document.getElementById("auth-tabs");
  const emailWrap = document.getElementById("auth-email-wrap");
  const otpWrap = document.getElementById("auth-otp-wrap");
  const otpActions = document.getElementById("auth-otp-actions");
  const otpNote = document.getElementById("auth-otp-note");
  const passwordWrap = document.getElementById("auth-password-wrap");
  const confirmWrap = document.getElementById("auth-confirm-wrap");
  const forgotLink = document.getElementById("auth-forgot-link");
  const backLink = document.getElementById("auth-back-login");
  const error = document.getElementById("auth-error");
  const note = document.querySelector("#auth-modal .auth-note");

  const isForgot = mode === "forgot";
  const isReset = mode === "reset";
  const isOtp = mode === "otp";
  const isAuth = mode === "login" || mode === "register";

  if (title) {
    if (mode === "forgot") title.textContent = t("auth.forgotTitle");
    else if (mode === "reset") title.textContent = t("auth.resetTitle");
    else if (mode === "otp") title.textContent = t("auth.otpTitle");
    else title.textContent = mode === "login" ? t("auth.login") : t("auth.register");
  }
  if (submit) {
    if (mode === "forgot") submit.textContent = t("auth.sendResetLink");
    else if (mode === "reset") submit.textContent = t("auth.setNewPassword");
    else if (mode === "otp") submit.textContent = t("auth.otpConfirm");
    else submit.textContent = mode === "login" ? t("auth.login") : t("auth.createAccount");
  }
  if (loginTab) loginTab.classList.toggle("active", mode === "login");
  if (registerTab) registerTab.classList.toggle("active", mode === "register");
  if (tabs) tabs.style.display = isAuth ? "" : "none";
  if (note) (note as HTMLElement).style.display = isAuth ? "" : "none";
  show(emailWrap as HTMLElement | null, !isReset && !isOtp);
  show(otpWrap as HTMLElement | null, isOtp);
  show(otpActions as HTMLElement | null, isOtp);
  show(passwordWrap as HTMLElement | null, isAuth || isReset);
  show(confirmWrap as HTMLElement | null, isReset);
  show(forgotLink as HTMLElement | null, mode === "login");
  show(backLink as HTMLElement | null, isForgot || isReset);
  if (error) error.textContent = "";
  if (otpNote && isOtp) {
    otpNote.textContent = t("auth.otpSent", { email: pendingRegistrationEmail ?? "" });
  }

  const emailInput = document.getElementById("auth-email") as HTMLInputElement | null;
  const otpInput = document.getElementById("auth-otp") as HTMLInputElement | null;
  const passwordInput = document.getElementById("auth-password") as HTMLInputElement | null;
  const confirmInput = document.getElementById("auth-confirm-password") as HTMLInputElement | null;
  if (emailInput) emailInput.required = !isReset && !isOtp;
  if (otpInput) otpInput.required = isOtp;
  if (passwordInput) passwordInput.required = isAuth || isReset;
  if (confirmInput) confirmInput.required = isReset;
  if (isOtp) otpInput?.focus();
}

export function setAuthResetToken(token: string | null) {
  pendingResetToken = token;
}

export function openAuthModal(
  mode: AuthMode = "login",
  message = "",
  intent: AuthIntent = "general",
) {
  if (mode === "register") {
    closeAuthModal();
    openCreateMemorial();
    return;
  }
  if (mode === "reset" && !pendingResetToken) {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("reset_token")?.trim();
    if (fromUrl) pendingResetToken = fromUrl;
  }
  authIntent = intent;
  if (intent !== "purchase") {
    mapState.pendingPurchaseHole = null;
  }
  setAuthMode(mode);
  const modal = document.getElementById("auth-modal");
  const error = document.getElementById("auth-error");
  if (error) error.textContent = message;
  if (modal) modal.style.display = "flex";
}

export function closeAuthModal() {
  const modal = document.getElementById("auth-modal");
  if (modal) modal.style.display = "none";
  pendingRegistrationEmail = null;
  resetAuthResumeState();
}

export function switchAuthMode(mode: "login" | "register") {
  if (mode === "register") {
    closeAuthModal();
    openCreateMemorial();
    return;
  }
  pendingRegistrationEmail = null;
  setAuthMode(mode);
}

export function openForgotPassword() {
  openAuthModal("forgot");
}

export function backToLogin() {
  pendingResetToken = null;
  clearResetTokenFromUrl();
  openAuthModal("login");
}

export function backToRegistration() {
  pendingRegistrationEmail = null;
  setAuthMode("register");
}

export async function resendRegistrationCode() {
  const email = pendingRegistrationEmail;
  const error = document.getElementById("auth-error");
  const button = document.getElementById("auth-otp-resend") as HTMLButtonElement | null;
  if (!email) {
    backToRegistration();
    return;
  }

  try {
    if (button) button.disabled = true;
    if (error) error.textContent = "";
    await resendRegisterOtp(email);
    if (error) error.textContent = t("auth.otpResent");
  } catch (err: any) {
    if (error) error.textContent = translateApiError(err) || t("auth.authFailed");
  } finally {
    if (button) button.disabled = false;
  }
}

function completeAuthentication() {
  const resumePurchase = authIntent === "purchase";
  const holeToOpen = resumePurchase ? mapState.pendingPurchaseHole : null;
  resetAuthResumeState();
  const modal = document.getElementById("auth-modal");
  if (modal) modal.style.display = "none";
  pendingRegistrationEmail = null;
  const emailInput = document.getElementById("auth-email") as HTMLInputElement | null;
  const passwordInput = document.getElementById("auth-password") as HTMLInputElement | null;
  const otpInput = document.getElementById("auth-otp") as HTMLInputElement | null;
  if (emailInput) emailInput.value = "";
  if (passwordInput) passwordInput.value = "";
  if (otpInput) otpInput.value = "";
  if (holeToOpen && !holeToOpen.classList.contains("bought")) {
    openPurchaseModal(holeToOpen);
  }
}

export async function submitAuthForm(event: Event) {
  event.preventDefault();

  if (authMode === "forgot") {
    await submitForgotPasswordForm();
    return;
  }
  if (authMode === "reset") {
    await submitResetPasswordForm();
    return;
  }
  if (authMode === "otp") {
    await submitRegistrationOtpForm();
    return;
  }

  const emailInput = document.getElementById(
    "auth-email",
  ) as HTMLInputElement | null;
  const passwordInput = document.getElementById(
    "auth-password",
  ) as HTMLInputElement | null;
  const error = document.getElementById("auth-error");
  const submit = document.getElementById(
    "auth-submit",
  ) as HTMLButtonElement | null;
  const email = emailInput?.value.trim() ?? "";
  const password = passwordInput?.value ?? "";

  if (!email || password.length < 6) {
    if (error) error.textContent = t("auth.invalidCredentials");
    return;
  }

  try {
    if (submit) submit.disabled = true;
    if (error) error.textContent = "";
    if (authMode === "login") {
      await login(email, password);
      completeAuthentication();
    } else {
      await startRegister(email, password);
      pendingRegistrationEmail = email.toLowerCase();
      if (passwordInput) passwordInput.value = "";
      setAuthMode("otp");
    }
  } catch (err: any) {
    if (error)
      error.textContent = translateApiError(err) || t("auth.authFailed");
  } finally {
    if (submit) submit.disabled = false;
  }
}

async function submitRegistrationOtpForm() {
  const email = pendingRegistrationEmail;
  const otpInput = document.getElementById("auth-otp") as HTMLInputElement | null;
  const error = document.getElementById("auth-error");
  const submit = document.getElementById("auth-submit") as HTMLButtonElement | null;
  const code = otpInput?.value.trim() ?? "";

  if (!email) {
    backToRegistration();
    return;
  }
  if (!/^\d{6}$/.test(code)) {
    if (error) error.textContent = t("auth.otpInvalid");
    return;
  }

  try {
    if (submit) submit.disabled = true;
    if (error) error.textContent = "";
    await verifyRegisterOtp(email, code);
    completeAuthentication();
  } catch (err: any) {
    if (error) error.textContent = translateApiError(err) || t("auth.otpInvalid");
  } finally {
    if (submit) submit.disabled = false;
  }
}

async function submitForgotPasswordForm() {
  const emailInput = document.getElementById("auth-email") as HTMLInputElement | null;
  const error = document.getElementById("auth-error");
  const submit = document.getElementById("auth-submit") as HTMLButtonElement | null;
  const email = emailInput?.value.trim() ?? "";

  if (!email) {
    if (error) error.textContent = t("auth.invalidCredentials");
    return;
  }

  try {
    if (submit) submit.disabled = true;
    if (error) error.textContent = "";
    await requestPasswordReset(email);
    if (error) error.textContent = t("auth.forgotSent");
    if (emailInput) emailInput.value = "";
  } catch (err: any) {
    if (error) error.textContent = translateApiError(err) || t("auth.authFailed");
  } finally {
    if (submit) submit.disabled = false;
  }
}

async function submitResetPasswordForm() {
  const passwordInput = document.getElementById("auth-password") as HTMLInputElement | null;
  const confirmInput = document.getElementById("auth-confirm-password") as HTMLInputElement | null;
  const error = document.getElementById("auth-error");
  const submit = document.getElementById("auth-submit") as HTMLButtonElement | null;
  const password = passwordInput?.value ?? "";
  const confirm = confirmInput?.value ?? "";
  const token = pendingResetToken;

  if (!token) {
    if (error) error.textContent = t("auth.resetTokenMissing");
    return;
  }
  if (password.length < 6) {
    if (error) error.textContent = t("auth.invalidCredentials");
    return;
  }
  if (password !== confirm) {
    if (error) error.textContent = t("auth.passwordMismatch");
    return;
  }

  try {
    if (submit) submit.disabled = true;
    if (error) error.textContent = "";
    await resetPassword(token, password);
    pendingResetToken = null;
    clearResetTokenFromUrl();
    closeAuthModal();
    if (passwordInput) passwordInput.value = "";
    if (confirmInput) confirmInput.value = "";
  } catch (err: any) {
    if (error) error.textContent = translateApiError(err) || t("auth.authFailed");
  } finally {
    if (submit) submit.disabled = false;
  }
}

export function updateAccountCard(user: SessionUser | null = getCurrentUser()): void {
  const btn = document.getElementById("account-btn");
  const label = document.getElementById("account-label");
  const sub = document.getElementById("account-sub");
  if (!btn) return;

  const isMobile = window.matchMedia("(max-width: 768px)").matches;
  const icon = btn.querySelector(".app-chrome__account-avatar i");

  if (user) {
    btn.title = t("toolbar.loggedInAs", { email: user.email });
    btn.classList.add("signed-in");
    if (label) {
      label.textContent = isMobile ? t("toolbar.myProfile") : user.email.split("@")[0] || user.email;
    }
    if (sub) sub.textContent = isMobile
      ? ""
      : user.pethernity?.active
        ? t("pethernity.title")
        : t("toolbar.viewProfile");
    if (icon) icon.setAttribute("data-lucide", "user");
  } else {
    btn.title = t("toolbar.loginOrRegister");
    btn.classList.remove("signed-in");
    if (label) label.textContent = isMobile ? t("toolbar.login") : t("toolbar.guest");
    if (sub) sub.textContent = isMobile ? "" : t("toolbar.loginOrRegister");
    if (icon) icon.setAttribute("data-lucide", "paw-print");
  }

  if (typeof lucide !== "undefined") lucide.createIcons();
}

export function refreshAuthButtonTitle(user: SessionUser | null = getCurrentUser()): void {
  updateAccountCard(user);
}

export function updateGardenProfilePill(user: SessionUser | null = getCurrentUser()): void {
  const pill = document.getElementById("garden-profile-pill");
  const label = document.getElementById("garden-profile-label");
  if (!pill || !label) return;

  if (user) {
    label.textContent = t("toolbar.myProfile");
    pill.title = t("toolbar.loggedInAs", { email: user.email });
    pill.classList.add("signed-in");
    const icon = pill.querySelector("i");
    if (icon) {
      icon.setAttribute("data-lucide", "user");
      if (typeof lucide !== "undefined") lucide.createIcons();
    }
  } else {
    label.textContent = t("toolbar.login");
    pill.title = t("toolbar.login");
    pill.classList.remove("signed-in");
    const icon = pill.querySelector("i");
    if (icon) {
      icon.setAttribute("data-lucide", "log-in");
      if (typeof lucide !== "undefined") lucide.createIcons();
    }
  }
}

function ensureVerificationBanner(): HTMLDivElement {
  if (verificationBanner) return verificationBanner;

  verificationBanner = document.createElement("div");
  verificationBanner.id = "email-verification-banner";
  verificationBanner.style.cssText =
    "position:fixed;left:50%;bottom:18px;transform:translateX(-50%);z-index:1200;" +
    "display:none;gap:12px;align-items:center;max-width:min(92vw,560px);padding:12px 16px;" +
    "border-radius:16px;background:#fff7e6;color:#4a2f00;box-shadow:0 6px 20px rgba(0,0,0,.18);" +
    "font-size:14px;line-height:1.35;";
  verificationBanner.innerHTML = `
    <span data-role="message"></span>
    <button type="button" data-role="resend" style="border:0;border-radius:12px;padding:8px 12px;background:#37474f;color:#fff;font-weight:bold;cursor:pointer;white-space:nowrap;"></button>
  `;

  const button = verificationBanner.querySelector('[data-role="resend"]') as HTMLButtonElement | null;
  button?.addEventListener("click", async () => {
    if (!button) return;
    try {
      button.disabled = true;
      await resendVerificationEmail();
      showToast(t("auth.verificationResent"), "info", 5000);
    } catch (err: any) {
      showToast(translateApiError(err) || t("auth.verificationResendFailed"));
    } finally {
      button.disabled = false;
    }
  });

  document.body.appendChild(verificationBanner);
  return verificationBanner;
}

function updateVerificationBanner(user: SessionUser | null): void {
  const banner = ensureVerificationBanner();
  const message = banner.querySelector('[data-role="message"]');
  const button = banner.querySelector('[data-role="resend"]');
  if (message) message.textContent = t("auth.verifyBanner");
  if (button) button.textContent = t("auth.resendVerification");
  banner.style.display = user && !user.emailVerified ? "flex" : "none";
}

export function initAuthUI(): void {
  const refreshAccount = (user: SessionUser | null) => {
    updateVerificationBanner(user);
    updateAccountCard(user);
    updateGardenProfilePill(user);
  };

  onUserChange(refreshAccount);
  refreshAccount(getCurrentUser());

  window.matchMedia("(max-width: 768px)").addEventListener("change", () => {
    updateAccountCard(getCurrentUser());
  });
}

export { getCurrentUser };
